import { combineData, data, type ObservableValue } from '@amazon/vinyl-observable'
import { type Maybe, noop } from '@amazon/vinyl-util'
import type { CreateCloudQueueOptions } from '../core/cloudQueueOptions'
import type { PlaylistController, QueueCapabilities } from '../core/PlaylistController'
import { addEntityToCloudQueue, insertEntityIntoCloudQueue } from './cloudQueueService'
import { removeFromCloudQueue, type ReorderQueueInput, reorderCloudQueue } from './queueViewService'

export interface LiveQueueState {
    readonly cloudQueueOptions: ObservableValue<Maybe<Pick<CreateCloudQueueOptions, 'queueId'>>>
    readonly queueCapabilities: ObservableValue<
        Maybe<Pick<QueueCapabilities, 'blockQueueMutations'>>
    >
}

export interface QueueEditorDeps {
    readonly playlistController: LiveQueueState & Pick<PlaylistController, 'notifyQueueMutated'>
    readonly refetchQueue: () => Promise<void>
}

/**
 * The one way to edit a cloud queue: edits run one at a time. An edit asked for while another is
 * in flight, or on the live queue while CloudQueue blocks its mutations, resolves false without
 * reaching the gateway, so a rapid second press is skipped rather than failed. Each edit resyncs the
 * playlist's `next` before it settles. A rejection is the edit's own failure: an edit that landed
 * counts as done even when the resync after it fails.
 */
export interface QueueEditor {
    /** True from an edit's first request until its resync settles. */
    readonly editing$: ObservableValue<boolean>
    /** The live queue's id while it accepts edits; null with no live queue or a blocked one. */
    editableQueueId(): string | null
    /** {@link editableQueueId} as an observable, for bindings. */
    readonly editableQueueId$: ObservableValue<string | null>
    /** False only while CloudQueue blocks edits; true with no queue yet, where Add to Queue plays instead. */
    canAddToQueue(): boolean
    readonly canAddToQueue$: ObservableValue<boolean>
    /**
     * Appends the entities in order. One that fails is skipped, and the edit rejects with the
     * first failure.
     */
    add(queueId: string, entityIds: readonly string[]): Promise<boolean>
    insert(
        queueId: string,
        entityId: string,
        anchorEntityReferenceId: string,
        placement: 'BEFORE' | 'AFTER'
    ): Promise<boolean>
    remove(queueId: string, entityReferenceIds: readonly string[]): Promise<boolean>
    reorder(input: ReorderQueueInput): Promise<boolean>
}

function editableQueueIdIn(
    options: Maybe<Pick<CreateCloudQueueOptions, 'queueId'>>,
    capabilities: Maybe<Pick<QueueCapabilities, 'blockQueueMutations'>>
): string | null {
    return capabilities?.blockQueueMutations === true ? null : (options?.queueId ?? null)
}

function canAddToQueueIn(capabilities: Maybe<Pick<QueueCapabilities, 'blockQueueMutations'>>) {
    return capabilities?.blockQueueMutations !== true
}

export function queueEditability(
    live: LiveQueueState
): Pick<QueueEditor, 'editableQueueId' | 'editableQueueId$' | 'canAddToQueue' | 'canAddToQueue$'> {
    return {
        canAddToQueue: () => canAddToQueueIn(live.queueCapabilities.value),
        canAddToQueue$: live.queueCapabilities.map(canAddToQueueIn),
        editableQueueId: () =>
            editableQueueIdIn(live.cloudQueueOptions.value, live.queueCapabilities.value),
        editableQueueId$: combineData({
            options: live.cloudQueueOptions,
            capabilities: live.queueCapabilities,
        }).map(({ options, capabilities }) => editableQueueIdIn(options, capabilities)),
    }
}

export function createQueueEditor(deps: QueueEditorDeps): QueueEditor {
    const { playlistController } = deps
    const editing$ = data(false)

    // Only the live queue's capabilities are known; a queue just created has not reached them yet.
    function blocked(queueId: string): boolean {
        return (
            playlistController.cloudQueueOptions.value?.queueId === queueId &&
            playlistController.queueCapabilities.value?.blockQueueMutations === true
        )
    }

    async function edit(queueId: string, run: () => Promise<void>): Promise<boolean> {
        if (editing$.value || blocked(queueId)) return false
        editing$.value = true
        try {
            await run()
            return true
        } finally {
            editing$.value = false
        }
    }

    const notifyQueueMutated = () => playlistController.notifyQueueMutated()
    // refetchQueue shows its own queue-update-failed notice.
    const refetchQueue = () => deps.refetchQueue().catch(noop)

    return {
        editing$,
        ...queueEditability(playlistController),
        add: (queueId, entityIds) =>
            edit(queueId, async () => {
                const failures: unknown[] = []
                for (const entityId of entityIds) {
                    await addEntityToCloudQueue(queueId, entityId).catch((error: unknown) => {
                        failures.push(error)
                    })
                }
                if (failures.length < entityIds.length) notifyQueueMutated()
                if (failures.length > 0) throw failures[0]
            }),
        insert: (queueId, entityId, anchorEntityReferenceId, placement) =>
            edit(queueId, async () => {
                await insertEntityIntoCloudQueue(
                    queueId,
                    entityId,
                    anchorEntityReferenceId,
                    placement
                )
                notifyQueueMutated()
            }),
        remove: (queueId, entityReferenceIds) =>
            edit(queueId, async () => {
                await removeFromCloudQueue(queueId, [...entityReferenceIds])
                await refetchQueue()
            }),
        reorder: (input) =>
            edit(input.queueId, async () => {
                await reorderCloudQueue(input)
                await refetchQueue()
            }),
    }
}
